import React, { useImperativeHandle, forwardRef, useRef, useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { common, createLowlight } from 'lowlight';
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';
import { FiBold, FiItalic, FiUnderline, FiList, FiCode, FiSmile, FiColumns, FiChevronDown, FiTrash2 } from 'react-icons/fi';
import EmojiPicker from 'emoji-picker-react';

const lowlight = createLowlight(common);

// Extension personnalisée : ajoute data-language sur <pre> pour le label CSS ::before
const CustomCodeBlock = CodeBlockLowlight.extend({
    renderHTML({ node, HTMLAttributes }) {
        return [
            'pre',
            mergeAttributes(HTMLAttributes, { 'data-language': node.attrs.language || '' }),
            ['code', { class: node.attrs.language ? `language-${node.attrs.language}` : '' }, 0],
        ];
    },
});

const LANGUAGES = [
    { value: '', label: 'Texte brut' },
    { value: 'javascript', label: 'JavaScript' },
    { value: 'typescript', label: 'TypeScript' },
    { value: 'sql', label: 'SQL' },
    { value: 'css', label: 'CSS' },
    { value: 'html', label: 'HTML' },
    { value: 'java', label: 'Java' },
    { value: 'python', label: 'Python' },
    { value: 'json', label: 'JSON' },
    { value: 'bash', label: 'Bash / Shell' },
    { value: 'xml', label: 'XML' },
    { value: 'yaml', label: 'YAML' },
    { value: 'csharp', label: 'C#' },
    { value: 'cpp', label: 'C++' },
    { value: 'php', label: 'PHP' },
];

const MAX_GRID = 8;

const TablePicker = ({ onInsert, onClose }) => {
    const [hovered, setHovered] = useState({ rows: 0, cols: 0 });

    return (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-xl p-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 font-semibold uppercase text-center">
                {hovered.rows > 0 && hovered.cols > 0
                    ? `${hovered.rows} lignes × ${hovered.cols} colonnes`
                    : 'Survoler pour choisir la taille'}
            </div>
            <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${MAX_GRID}, 1.4rem)` }}>
                {Array.from({ length: MAX_GRID * MAX_GRID }).map((_, i) => {
                    const row = Math.floor(i / MAX_GRID) + 1;
                    const col = (i % MAX_GRID) + 1;
                    const isActive = row <= hovered.rows && col <= hovered.cols;
                    return (
                        <div
                            key={i}
                            onMouseEnter={() => setHovered({ rows: row, cols: col })}
                            onClick={() => {
                                if (hovered.rows > 0 && hovered.cols > 0) {
                                    onInsert(hovered.rows, hovered.cols);
                                    onClose();
                                }
                            }}
                            className={`w-5 h-5 border rounded-sm cursor-pointer transition-all ${
                                isActive
                                    ? 'bg-primary border-primary opacity-80'
                                    : 'border-gray-300 dark:border-gray-600 hover:border-primary'
                            }`}
                        />
                    );
                })}
            </div>
        </div>
    );
};

const MenuBar = ({ editor, onEmojiClick }) => {
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showCodeLangPicker, setShowCodeLangPicker] = useState(false);
    const [showTablePicker, setShowTablePicker] = useState(false);
    const emojiButtonRef = useRef(null);
    const codeLangRef = useRef(null);
    const tableRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (codeLangRef.current && !codeLangRef.current.contains(e.target)) {
                setShowCodeLangPicker(false);
            }
            if (tableRef.current && !tableRef.current.contains(e.target)) {
                setShowTablePicker(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (!editor) return null;

    const buttonClass = (isActive) => `
        p-2 rounded transition-colors
        ${isActive
            ? 'bg-primary text-white dark:text-black'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
        }
    `;

    const activeCodeLang = editor.isActive('codeBlock')
        ? (editor.getAttributes('codeBlock').language || '')
        : '';
    const activeLangLabel = LANGUAGES.find(l => l.value === activeCodeLang)?.label || 'Code';

    return (
        <div className="border-b dark:border-gray-700 p-2 flex flex-wrap gap-2 relative">
            {/* Bold */}
            <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={buttonClass(editor.isActive('bold'))} title="Gras (Ctrl+B)">
                <FiBold className="w-4 h-4" />
            </button>

            {/* Italic */}
            <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={buttonClass(editor.isActive('italic'))} title="Italique (Ctrl+I)">
                <FiItalic className="w-4 h-4" />
            </button>

            {/* Underline */}
            <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()} className={buttonClass(editor.isActive('underline'))} title="Souligné (Ctrl+U)">
                <FiUnderline className="w-4 h-4" />
            </button>

            <div className="w-px bg-gray-300 dark:bg-gray-600" />

            {/* Bullet List */}
            <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={buttonClass(editor.isActive('bulletList'))} title="Liste à puces">
                <FiList className="w-4 h-4" />
            </button>

            <div className="w-px bg-gray-300 dark:bg-gray-600" />

            {/* Code block with language picker */}
            <div className="relative" ref={codeLangRef}>
                <button
                    type="button"
                    onClick={() => {
                        if (!editor.isActive('codeBlock')) {
                            editor.chain().focus().setCodeBlock({ language: '' }).run();
                        }
                        setShowCodeLangPicker(!showCodeLangPicker);
                    }}
                    className={`${buttonClass(editor.isActive('codeBlock'))} flex items-center gap-1`}
                    title="Bloc de code avec syntaxe"
                >
                    <FiCode className="w-4 h-4" />
                    {editor.isActive('codeBlock') && (
                        <span className="text-xs font-mono max-w-[56px] truncate leading-none">{activeLangLabel}</span>
                    )}
                    <FiChevronDown className="w-3 h-3 opacity-70" />
                </button>

                {showCodeLangPicker && (
                    <div className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-xl py-1 min-w-[170px]">
                        <div className="px-3 py-1 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase">Langage</div>
                        {LANGUAGES.map(lang => (
                            <button
                                key={lang.value}
                                type="button"
                                onClick={() => {
                                    if (editor.isActive('codeBlock')) {
                                        editor.chain().focus().updateAttributes('codeBlock', { language: lang.value }).run();
                                    } else {
                                        editor.chain().focus().setCodeBlock({ language: lang.value }).run();
                                    }
                                    setShowCodeLangPicker(false);
                                }}
                                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between transition-colors ${
                                    activeCodeLang === lang.value
                                        ? 'font-semibold text-primary'
                                        : 'text-gray-700 dark:text-gray-300'
                                }`}
                            >
                                <span>{lang.label}</span>
                                {activeCodeLang === lang.value && <span className="text-xs">✓</span>}
                            </button>
                        ))}
                        {editor.isActive('codeBlock') && (
                            <>
                                <div className="border-t dark:border-gray-700 my-1" />
                                <button
                                    type="button"
                                    onClick={() => {
                                        editor.chain().focus().toggleCodeBlock().run();
                                        setShowCodeLangPicker(false);
                                    }}
                                    className="w-full text-left px-3 py-1.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                >
                                    Supprimer le bloc
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Table picker */}
            <div className="relative" ref={tableRef}>
                <button
                    type="button"
                    onClick={() => setShowTablePicker(!showTablePicker)}
                    className={buttonClass(editor.isActive('table'))}
                    title="Insérer un tableau"
                >
                    <FiColumns className="w-4 h-4" />
                </button>
                {showTablePicker && (
                    <TablePicker
                        onInsert={(rows, cols) => {
                            editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
                        }}
                        onClose={() => setShowTablePicker(false)}
                    />
                )}
            </div>

            {/* Supprimer le tableau (visible uniquement quand le curseur est dans un tableau) */}
            {editor.isActive('table') && (
                <>
                    <div className="w-px bg-gray-300 dark:bg-gray-600" />
                    <button
                        type="button"
                        onClick={() => editor.chain().focus().deleteTable().run()}
                        className="p-1.5 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                        title="Supprimer le tableau"
                    >
                        <FiTrash2 className="w-4 h-4" />
                    </button>
                </>
            )}

            <div className="w-px bg-gray-300 dark:bg-gray-600" />

            {/* Emoji */}
            <button
                ref={emojiButtonRef}
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className={buttonClass(showEmojiPicker)}
                title="Emojis"
            >
                <FiSmile className="w-4 h-4" />
            </button>

            {showEmojiPicker && (
                <div
                    className="absolute top-full left-0 mt-1 z-50 w-72 md:w-96 lg:w-[550px]"
                    style={{ position: 'absolute', zIndex: 9999 }}
                >
                    <div className="relative">
                        <button
                            type="button"
                            onClick={() => setShowEmojiPicker(false)}
                            className="absolute -top-2 -right-2 bg-gray-700 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-gray-600 z-10"
                            style={{ zIndex: 10000 }}
                        >
                            ×
                        </button>
                        <EmojiPicker
                            onEmojiClick={(emojiData) => {
                                onEmojiClick(emojiData.emoji);
                                setShowEmojiPicker(false);
                            }}
                            theme="auto"
                            height={350}
                            width="100%"
                            searchPlaceHolder="Rechercher..."
                            previewConfig={{ showPreview: false }}
                        />
                    </div>
                </div>
            )}

            <div className="w-px bg-gray-300 dark:bg-gray-600" />

            {/* Clear formatting */}
            <button
                type="button"
                onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
                className="p-2 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 text-xs"
                title="Effacer le formatage"
            >
                Effacer
            </button>
        </div>
    );
};

const RichTextEditor = forwardRef(({
    content = '',
    onChange,
    placeholder = 'Écrivez ici...',
    editable = true,
    className = '',
    minHeight = '150px',
    onMentionTrigger,
    onDateTrigger
}, ref) => {
    const containerRef = useRef(null);
    const mentionStartRef = useRef(null);

    // Refs pour éviter le stale closure dans le onUpdate de Tiptap :
    // useEditor capture les callbacks au montage, sans les mettre à jour quand les
    // props changent (ex: allMessages vide → chargé). Les refs sont toujours à jour.
    const onMentionTriggerRef = useRef(onMentionTrigger);
    const onDateTriggerRef = useRef(onDateTrigger);
    onMentionTriggerRef.current = onMentionTrigger;
    onDateTriggerRef.current = onDateTrigger;

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: false,
                bold: true,
                italic: true,
                strike: false,
                underline: false,
                codeBlock: false, // remplacé par CodeBlockLowlight
            }),
            Underline,
            Placeholder.configure({
                placeholder,
                emptyEditorClass: 'is-editor-empty',
            }),
            CustomCodeBlock.configure({
                lowlight,
                defaultLanguage: null,
            }),
            Table.configure({ resizable: true }),
            TableRow,
            TableHeader,
            TableCell,
        ],
        content,
        editable,
        onUpdate: ({ editor }) => {
            if (onChange) {
                onChange(editor.getHTML());
            }
            checkForMentionTrigger(editor);
        },
        editorProps: {
            attributes: {
                class: 'prose prose-sm dark:prose-invert max-w-none focus:outline-none px-3 py-2 max-w-none max-h-[25vh] overflow-auto user-mention-container',
            },
            handleKeyDown: (view, event) => {
                if (event.key === '@') {
                    setTimeout(() => {
                        const { from } = view.state.selection;
                        mentionStartRef.current = from;
                    }, 0);
                }
                return false;
            },
        },
    });

    const checkForMentionTrigger = (editor) => {
        if (!containerRef.current) return;

        const { from } = editor.state.selection;
        const textBefore = editor.state.doc.textBetween(Math.max(0, from - 50), from, '\n');
        const editorRect = containerRef.current.getBoundingClientRect();

        // ── Déclencheur @ (mentions) ──────────────────────────────────────
        if (onMentionTriggerRef.current) {
            const lastAtIndex = textBefore.lastIndexOf('@');
            if (lastAtIndex !== -1) {
                const query = textBefore.slice(lastAtIndex + 1);
                if (!/\s/.test(query)) {
                    onMentionTriggerRef.current({
                        query,
                        startPos: mentionStartRef.current || from - query.length - 1,
                        rect: editorRect,
                        isActive: true
                    });
                } else {
                    onMentionTriggerRef.current({ isActive: false });
                }
            } else {
                onMentionTriggerRef.current({ isActive: false });
            }
        }

        // ── Déclencheur // (dates dynamiques) ────────────────────────────
        if (onDateTriggerRef.current) {
            const lastDSlash = textBefore.lastIndexOf('//');
            if (lastDSlash !== -1) {
                const query = textBefore.slice(lastDSlash + 2);
                if (!/\s/.test(query)) {
                    onDateTriggerRef.current({ query, rect: editorRect, isActive: true });
                } else {
                    onDateTriggerRef.current({ isActive: false });
                }
            } else {
                onDateTriggerRef.current({ isActive: false });
            }
        }
    };

    useEffect(() => {
        if (editor && content !== editor.getHTML()) {
            editor.commands.setContent(content);
        }
    }, [content, editor]);

    useImperativeHandle(ref, () => ({
        insertMention: (mention) => {
            if (!editor) return;

            const { from } = editor.state.selection;
            const textBefore = editor.state.doc.textBetween(Math.max(0, from - 50), from, '\n');
            const lastAtIndex = textBefore.lastIndexOf('@');

            if (lastAtIndex !== -1) {
                const deleteFrom = from - (textBefore.length - lastAtIndex);
                const deleteTo = from;

                editor
                    .chain()
                    .focus()
                    .deleteRange({ from: deleteFrom, to: deleteTo })
                    .insertContent(mention + ' ')
                    .run();
            }
        },
        insertDateCommand: (value) => {
            if (!editor) return;

            const { from } = editor.state.selection;
            const textBefore = editor.state.doc.textBetween(Math.max(0, from - 50), from, '\n');
            const lastDSlash = textBefore.lastIndexOf('//');

            if (lastDSlash !== -1) {
                const deleteFrom = from - (textBefore.length - lastDSlash);
                const deleteTo = from;
                editor
                    .chain()
                    .focus()
                    .deleteRange({ from: deleteFrom, to: deleteTo })
                    .insertContent(value)
                    .run();
            }
        },
        focus: () => {
            editor?.commands.focus();
        },
        isEmpty: () => {
            if (!editor) return true;
            const text = editor.getText().trim();
            return !text || text.length === 0;
        }
    }), [editor]);

    const handleEmojiClick = (emoji) => {
        if (editor) {
            editor.chain().focus().insertContent(emoji).run();
        }
    };

    return (
        <div
            ref={containerRef}
            className={`border dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 ${className}`}
        >
            {editable && <MenuBar editor={editor} onEmojiClick={handleEmojiClick} />}
            <EditorContent
                editor={editor}
                style={{ minHeight }}
                className="text-black dark:text-white"
            />
        </div>
    );
});

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;